const DEFAULT_ACCENT = '#228be6'
const DATE_FORMAT = { month: 'long', day: 'numeric', year: 'numeric' }

export const getSafeColor = (value, fallback = DEFAULT_ACCENT) =>
	typeof value === 'string' &&
	/^(#[\da-f]{3,8}|rgb\(|hsl\()/i.test(value.trim())
		? value.trim()
		: fallback

export const getContrastTextColor = (value) => {
	if (typeof value !== 'string') return '#ffffff'
	const match = value.trim().match(/^#([\da-f]{3}|[\da-f]{6})$/i)
	if (!match) return '#ffffff'

	const hex =
		match[1].length === 3
			? match[1]
					.split('')
					.map((character) => character.repeat(2))
					.join('')
			: match[1]
	const [red, green, blue] = [0, 2, 4].map((offset) =>
		Number.parseInt(hex.slice(offset, offset + 2), 16),
	)
	const luminance = (red * 299 + green * 587 + blue * 114) / 1000
	return luminance > 150 ? '#171a27' : '#ffffff'
}

export const resolveTravelerExperienceDates = (
	checkIn = {},
	experience = {},
) => ({
	checkInDate: checkIn.scheduledCheckInDate || experience.startDate || null,
	checkOutDate: checkIn.checkOutDate || experience.endDate || null,
})

export const formatDate = (value, options = DATE_FORMAT) => {
	if (!value) return null
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return null
	return date.toLocaleDateString('en-US', { ...options, timeZone: 'UTC' })
}

export const formatTripRange = (startValue, endValue) => {
	const start = formatDate(startValue, { month: 'long', day: 'numeric' })
	const end = formatDate(endValue, {
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	})
	if (start && end) return `${start} – ${end}`
	return start || end || 'Dates to be announced'
}

export const formatClockTime = (value) => {
	if (typeof value !== 'string' || !/^\d{2}:\d{2}$/.test(value)) return null
	const [hours, minutes] = value.split(':').map(Number)
	if (hours > 23 || minutes > 59) return null
	const suffix = hours >= 12 ? 'PM' : 'AM'
	const displayHour = hours % 12 || 12
	return `${displayHour}:${String(minutes).padStart(2, '0')} ${suffix}`
}

export const formatCheckInTimes = (entries) =>
	(Array.isArray(entries) ? entries : [])
		.map((entry) => {
			const start = formatClockTime(entry?.start)
			const end = formatClockTime(entry?.end)
			if (!start || !end) return null
			return `${entry?.label ? `${entry.label}: ` : ''}${start} – ${end}`
		})
		.filter(Boolean)

export const formatCustomFieldValue = (value) => {
	if (value === null || value === undefined) return ''
	if (typeof value === 'string') return value.trim()
	if (typeof value === 'number' || typeof value === 'boolean') return String(value)
	if (Array.isArray(value)) return value.map(formatCustomFieldValue).filter(Boolean).join(', ')
	try {
		return JSON.stringify(value)
	} catch {
		return String(value)
	}
}

export const getTravelerStationLabel = (value) => {
	const override = typeof value === 'string' ? value.trim() : ''
	return override ? `Your ${override}` : 'Check-in Location'
}

export const getTripStatus = (
	checkInDateValue,
	checkOutDateValue,
	now = new Date(),
) => {
	if (!checkInDateValue) return 'TRIP DETAILS'

	const checkInDate = new Date(checkInDateValue)
	const checkOutDate = checkOutDateValue ? new Date(checkOutDateValue) : null
	if (Number.isNaN(checkInDate.getTime())) return 'TRIP DETAILS'

	const todayValue = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
	const checkInValue = Date.UTC(
		checkInDate.getUTCFullYear(),
		checkInDate.getUTCMonth(),
		checkInDate.getUTCDate(),
	)
	const checkOutValue =
		checkOutDate && !Number.isNaN(checkOutDate.getTime())
			? Date.UTC(
					checkOutDate.getUTCFullYear(),
					checkOutDate.getUTCMonth(),
					checkOutDate.getUTCDate(),
				)
			: null

	if (todayValue < checkInValue) return 'UPCOMING EXPERIENCE'
	if (checkOutValue !== null && todayValue > checkOutValue) {
		return 'EXPERIENCE COMPLETE'
	}
	return 'EXPERIENCE IN PROGRESS'
}
